/**
 * Groups attribution entries by the thing that granted them, so the tab reads
 * "scheduleEditor ← FIQT3" rather than a flat list of demands.
 *
 * Entries arrive already attributed once per source type (see attributeOnce in
 * library/grants/access-sources.mjs), so a product whose demands are wholly covered by a
 * broader one the client also holds contributes no entries and produces no group here.
 */
export function groupBySource(entries) {
    const groups = new Map()

    for (const entry of entries ?? []) {
        // Level is part of the key: the same source name at client and user level is two
        // different facts, and you change them in different places.
        const key = `${entry.level}:${entry.source}:${entry.sourceId}`
        if (!groups.has(key)) {
            groups.set(key, {
                source: entry.source,
                sourceId: entry.sourceId,
                sourceLabel: entry.sourceLabel,
                origin: entry.origin,
                description: entry.description,
                level: entry.level,
                demands: [],
            })
        }
        const group = groups.get(key)
        if (!group.demands.includes(entry.demand)) group.demands.push(entry.demand)
    }

    return [...groups.values()].sort(bySourceThenBreadth)
}

/**
 * Splits attributed groups into Client and User sections.
 *
 * Where a demand is granted — on the client or on the user — is the first thing you need when
 * you want to change it, so it is structure rather than a label on every row. Levels with
 * nothing in them are omitted, so the client tab (which has no user sources) does not render an
 * empty section.
 */
export function groupByLevel(groups) {
    return [LEVEL_CLIENT, LEVEL_USER]
        .map((level) => ({ level, groups: groups.filter((group) => group.level === level) }))
        .filter((section) => section.groups.length > 0)
}

export const LEVEL_CLIENT = "client"
export const LEVEL_USER = "user"

export const LEVEL_HEADINGS = {
    client: "Client",
    user: "User",
}

export const SOURCE_ORDER = [
    "sku",
    "bundle",
    "clientDemands",
    "licenceType",
    "role",
    "userDemands",
    "userMetaDemands",
    "grant",
]

export const SOURCE_LABELS = {
    sku: "Product",
    bundle: "Auth bundle",
    clientDemands: "Client record",
    licenceType: "Licence type",
    role: "Role",
    userDemands: "User record demands",
    userMetaDemands: "User metadata demands",
    grant: "Override",
}

/**
 * Where a product grant came from. A client can hold the same family of product from more than
 * one source — a real HubSpot FIQT1 alongside a FubSpot FIQT3, say — and their seat and regime
 * allowances are SUMMED, not superseded. Showing the origin is what stops that looking like a bug.
 */
export const ORIGIN_LABELS = {
    hubspot: "HubSpot",
    fubspot: "FubSpot",
    personal: "Personal / Stripe",
    "fubspot-personal": "FubSpot personal",
    unknown: "unknown origin",
}

/**
 * Source type first, then BREADTH — most demands first.
 *
 * Alphabetical put FIQT1 above FIQT3, which reads as though the lower tier were the primary
 * entitlement. Sorting by demand count puts the product doing the most work at the top, and
 * falls out to T3 → T2 → T1 on its own without hardcoding tier names or needing a new rule each
 * time a product is added. It also makes the de-duplication above attribute to the broadest.
 */
function bySourceThenBreadth(a, b) {
    const order = SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source)
    if (order !== 0) return order
    const breadth = (b.demands?.length ?? 0) - (a.demands?.length ?? 0)
    return breadth !== 0 ? breadth : String(a.sourceLabel).localeCompare(String(b.sourceLabel))
}
